import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ChevronLeft, ChevronRight, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { JOB_STATUS_FLOW } from "@/lib/constants";
import { useAuth, hasRole } from "@/contexts/AuthContext";

const EVENT_LABELS = {
  measurement: "Ölçü",
  order_delivery: "Sipariş Teslim",
  install_start: "Montaj Başlangıç",
  install_end: "Montaj Bitiş",
};

const EVENT_COLORS = {
  measurement: "bg-[#EAF7FF] text-[#168DEB] border-[#BDE3FF]",
  order_delivery: "bg-purple-50 text-purple-800 border-purple-200",
  install_start: "bg-emerald-50 text-emerald-800 border-emerald-200",
  install_end: "bg-amber-50 text-amber-800 border-amber-200",
};

function startOfWeek(d) {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7; // Monday = 0
  x.setDate(x.getDate() - day);
  x.setHours(0, 0, 0, 0);
  return x;
}

function startOfMonth(d) {
  const x = new Date(d);
  x.setDate(1);
  x.setHours(0, 0, 0, 0);
  return x;
}

function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

function fmtISO(d) { return d.toISOString(); }

function fmtDay(d) {
  return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

export default function CalendarPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [view, setView] = useState("month"); // month | week
  const [anchor, setAnchor] = useState(() => new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [salespersonId, setSalespersonId] = useState("");
  const [productCategoryId, setProductCategoryId] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [salespeople, setSalespeople] = useState([]);
  const [productCategories, setProductCategories] = useState([]);

  const range = useMemo(() => {
    if (view === "week") {
      const start = startOfWeek(anchor);
      const end = addDays(start, 6);
      end.setHours(23, 59, 59, 999);
      return { start, end };
    }
    const start = startOfMonth(anchor);
    const gridStart = startOfWeek(start);
    const gridEnd = addDays(gridStart, 41);
    gridEnd.setHours(23, 59, 59, 999);
    return { start: gridStart, end: gridEnd };
  }, [view, anchor]);

  const loadRefs = useCallback(async () => {
    try {
      const [pcs, sps] = await Promise.all([
        api.get("/product-categories"),
        hasRole(user, "admin") ? api.get("/users/salespeople") : Promise.resolve({ data: [] }),
      ]);
      setProductCategories(pcs.data);
      setSalespeople(sps.data);
    } catch (_) {}
  }, [user]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        date_from: fmtISO(range.start),
        date_to: fmtISO(range.end),
      };
      if (salespersonId) params.salesperson_id = salespersonId;
      if (productCategoryId) params.product_category_id = productCategoryId;
      if (statusFilter) params.status = statusFilter;
      const r = await api.get("/jobs/calendar-events", { params });
      setEvents(r.data.events);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [range.start, range.end, salespersonId, productCategoryId, statusFilter]);

  useEffect(() => { loadRefs(); }, [loadRefs]);
  useEffect(() => { load(); }, [load]);

  const shift = (dir) => {
    const next = new Date(anchor);
    if (view === "week") next.setDate(next.getDate() + dir * 7);
    else next.setMonth(next.getMonth() + dir);
    setAnchor(next);
  };

  const daysInRange = useMemo(() => {
    const days = [];
    let cursor = new Date(range.start);
    while (cursor <= range.end) {
      days.push(new Date(cursor));
      cursor = addDays(cursor, 1);
    }
    return days;
  }, [range.start, range.end]);

  const eventsByDay = useMemo(() => {
    const map = new Map();
    for (const ev of events) {
      const d = new Date(ev.date);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(ev);
    }
    return map;
  }, [events]);

  const monthLabel = anchor.toLocaleString("tr-TR", { month: "long", year: "numeric" });

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 bg-[#F4F6F8] min-h-full" data-testid="calendar-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#171A1F] tracking-tight">Operasyon Takvimi</h1>
          <p className="text-xs text-slate-500 mt-1">Ölçü, sipariş teslim, montaj başlangıç ve bitiş kayıtları.</p>
        </div>
        <div className="inline-flex items-center rounded-md border border-[#DCE2E8] overflow-hidden shadow-sm">
          <button
            type="button"
            onClick={() => setView("week")}
            className={`px-4 py-1.5 text-sm font-medium ${view === "week" ? "bg-[#FF353A] text-white" : "bg-white text-[#171A1F]"}`}
            data-testid="calendar-view-week"
          >Haftalık</button>
          <button
            type="button"
            onClick={() => setView("month")}
            className={`px-4 py-1.5 text-sm font-medium ${view === "month" ? "bg-[#FF353A] text-white" : "bg-white text-[#171A1F]"}`}
            data-testid="calendar-view-month"
          >Aylık</button>
        </div>
      </div>

      <div className="rounded-lg border border-[#DCE2E8] bg-white p-4 grid gap-3 md:grid-cols-5 items-center shadow-sm">
        <div className="inline-flex items-center gap-2 md:col-span-2">
          <Button variant="outline" size="sm" className="border-[#DCE2E8]" onClick={() => shift(-1)} data-testid="calendar-prev"><ChevronLeft size={14} /></Button>
          <span className="text-sm font-bold text-[#171A1F] min-w-[200px] text-center capitalize" data-testid="calendar-anchor">
            {view === "week"
              ? `${fmtDay(range.start)} — ${fmtDay(range.end)} · ${range.start.getFullYear()}`
              : monthLabel}
          </span>
          <Button variant="outline" size="sm" className="border-[#DCE2E8]" onClick={() => shift(1)} data-testid="calendar-next"><ChevronRight size={14} /></Button>
          <Button variant="outline" size="sm" className="border-[#DCE2E8]" onClick={() => setAnchor(new Date())} data-testid="calendar-today">Bugün</Button>
        </div>
        <Select value={statusFilter || "__all__"} onValueChange={(v) => setStatusFilter(v === "__all__" ? "" : v)}>
          <SelectTrigger className="border-[#DCE2E8]" data-testid="calendar-status-filter"><SelectValue placeholder="Durum" /></SelectTrigger>
          <SelectContent className="bg-white border-[#DCE2E8]">
            <SelectItem value="__all__">Tüm Durumlar</SelectItem>
            {JOB_STATUS_FLOW.map(([k, l]) => (<SelectItem key={k} value={k}>{l}</SelectItem>))}
          </SelectContent>
        </Select>
        <Select value={productCategoryId || "__all__"} onValueChange={(v) => setProductCategoryId(v === "__all__" ? "" : v)}>
          <SelectTrigger className="border-[#DCE2E8]" data-testid="calendar-product-filter"><SelectValue placeholder="Ürün Grubu" /></SelectTrigger>
          <SelectContent className="bg-white border-[#DCE2E8]">
            <SelectItem value="__all__">Tüm Ürünler</SelectItem>
            {productCategories.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}
          </SelectContent>
        </Select>
        {hasRole(user, "admin") ? (
          <Select value={salespersonId || "__all__"} onValueChange={(v) => setSalespersonId(v === "__all__" ? "" : v)}>
            <SelectTrigger className="border-[#DCE2E8]" data-testid="calendar-salesperson-filter"><SelectValue placeholder="Satış Personeli" /></SelectTrigger>
            <SelectContent className="bg-white border-[#DCE2E8]">
              <SelectItem value="__all__">Tüm Personel</SelectItem>
              {salespeople.map((sp) => (<SelectItem key={sp.id} value={sp.id}>{sp.full_name}</SelectItem>))}
            </SelectContent>
          </Select>
        ) : (
          <div className="text-xs text-slate-500 ml-auto font-medium">Toplam {events.length} kayıt</div>
        )}
      </div>

      <div className="rounded-lg border border-[#DCE2E8] bg-white shadow-sm overflow-x-auto scrollbar-thin">
        <div className="min-w-[640px]">
          <div className="grid grid-cols-7 border-b border-[#DCE2E8] bg-[#F4F6F8]">
            {["Pzt", "Sal", "Çrş", "Prş", "Cum", "Cmt", "Paz"].map((d) => (
              <div key={d} className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-600 text-center">{d}</div>
            ))}
          </div>
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-16 text-slate-500"><Loader2 className="animate-spin text-[#FF353A]" size={16} /> Yükleniyor...</div>
          ) : (
            <div className="grid grid-cols-7 auto-rows-fr">
              {daysInRange.map((d, idx) => {
                const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
                const evs = eventsByDay.get(key) || [];
                const isCurrentMonth = view === "week" || d.getMonth() === anchor.getMonth();
                const today = isSameDay(d, new Date());
                return (
                  <div
                    key={idx}
                    className={`min-h-[100px] p-1.5 border-r border-b border-[#DCE2E8] ${!isCurrentMonth ? "bg-[#F4F6F8]/50" : "bg-white"}`}
                    data-testid={`calendar-day-${d.toISOString().slice(0, 10)}`}
                  >
                    <div className={`text-[11px] font-medium mb-1 ${today ? "text-white bg-[#FF353A] rounded-full h-5 w-5 inline-flex items-center justify-center font-bold" : isCurrentMonth ? "text-slate-700" : "text-slate-400"}`}>
                      {d.getDate()}
                    </div>
                    <div className="space-y-1">
                      {evs.slice(0, 5).map((ev, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => navigate(`/isler/${ev.job_id}`)}
                          className={`w-full text-left rounded px-1.5 py-1 border text-[10px] leading-tight hover:opacity-80 transition-opacity ${EVENT_COLORS[ev.type]}`}
                          data-testid={`calendar-event-${ev.type}-${ev.job_number}`}
                        >
                          <div className="font-mono font-semibold truncate">{ev.job_number} · {EVENT_LABELS[ev.type]}</div>
                          <div className="truncate">{ev.customer_name}</div>
                          {ev.is_installation_delayed && (
                            <span className="inline-flex items-center gap-0.5 text-rose-700 font-semibold mt-0.5">
                              <AlertTriangle size={9} /> Gecikmiş
                            </span>
                          )}
                        </button>
                      ))}
                      {evs.length > 5 && (
                        <div className="text-[10px] text-slate-500 px-1 font-medium">+{evs.length - 5} kayıt</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Legend */}
      <div className="rounded-lg border border-[#DCE2E8] bg-white p-3 flex flex-wrap gap-3 text-xs text-slate-600 shadow-sm">
        <span className="font-semibold text-[#171A1F] mr-1">Etkinlik Türleri:</span>
        {Object.entries(EVENT_LABELS).map(([k, l]) => (
          <span key={k} className={`inline-flex items-center rounded px-2 py-0.5 border font-medium ${EVENT_COLORS[k]}`}>{l}</span>
        ))}
        <span className="ml-auto inline-flex items-center gap-1 text-rose-700 font-medium"><AlertTriangle size={11} /> Gecikmiş montajlar kırmızı etiketli</span>
      </div>
    </div>
  );
}
